import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M19 L19.07 — Impersonation governance (administrator acting as a user).
 *
 * Lists impersonation sessions from `GET /v1/platform/impersonation`, allows an
 * administrator to start a governed, audited impersonation session
 * (`POST` with targetUserId + mandatory reason) and to stop an active one
 * (`POST :id/stop`).
 *
 * Security: starting a session records and audits intent only. A scoped
 * "act-as" credential is deliberately NOT issued from this admin screen — that
 * is an M02 IAM concern pending a dedicated security review.
 */
interface ImpersonationRow {
  readonly privilegedAccessId: string;
  readonly subjectName: string | null;
  readonly subjectCode: string | null;
  readonly createdByName: string | null;
  readonly createdByCode: string | null;
  readonly accessReason: string;
  readonly status: string;
  readonly grantedAt: string;
  readonly expiresAt?: string | null;
}

function statusColor(status: string): string {
  const s = status.toUpperCase();
  if (s === 'ACTIVE') return 'var(--color-success)';
  if (s === 'TERMINATED') return 'var(--color-text-muted)';
  return 'var(--color-danger)';
}

export function ImpersonationPage(): ReactNode {
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<ImpersonationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [targetUserId, setTargetUserId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    let alive = true;
    apiRequest<{ data: ImpersonationRow[] }>('/v1/platform/impersonation', { token })
      .then((r) => { if (alive) setRows(r.data ?? []); })
      .catch((e) => { if (alive) { setError((e as Error).message); setRows([]); } });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => load(), [load]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!targetUserId.trim() || !reason.trim()) {
      setError(t('m19.impersonation.validationRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await apiRequest('/v1/platform/impersonation', {
        method: 'POST',
        token,
        body: { targetUserId: targetUserId.trim(), reason: reason.trim() },
      });
      setTargetUserId(''); setReason('');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const stop = async (id: string) => {
    setError(null);
    setBusyId(id);
    try {
      await apiRequest(`/v1/platform/impersonation/${id}/stop`, {
        method: 'POST',
        token,
        body: { reason: 'Ended by administrator' },
      });
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const inputStyle = { padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' } as const;

  return (
    <main aria-label="impersonation">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('m19.impersonation.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>
        {t('m19.impersonation.subtitle')}
      </p>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}

      <form aria-label="impersonation-form" onSubmit={submit}
            style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 24, maxWidth: 900 }}>
        <div style={{ flex: '1 1 260px' }}>
          <RecordPicker<{ userId: string; displayName?: string; employeeCode?: string }>
            endpoint="/v1/users?limit=200"
            idField="userId"
            label={t('m19.impersonation.labelTarget')}
            testId="target-user"
            value={targetUserId}
            optionLabel={(u) =>
              [u.employeeCode, u.displayName].filter(Boolean).join(' \u2014 ') || u.userId
            }
            onPick={setTargetUserId}
            emptyText="No user on file"
          />
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, flex: '1 1 320px' }}>
          {t('m19.impersonation.labelReason')}
          <input aria-label="reason" value={reason} onChange={(e) => setReason(e.target.value)} style={inputStyle} />
        </label>
        <button type="submit" data-testid="submit-impersonation" disabled={submitting}
                style={{ padding: '8px 16px', borderRadius: 4, border: '1px solid var(--color-brand-primary)', background: 'var(--color-brand-primary)', color: 'var(--color-text-inverse)' }}>
          {submitting ? t('m19.impersonation.starting') : t('m19.impersonation.start')}
        </button>
      </form>

      {!rows ? (
        <p>{t('m19.impersonation.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="impersonation-empty" style={{ color: 'var(--color-text-muted)' }}>{t('m19.impersonation.empty')}</p>
      ) : (
        <table aria-label="impersonation-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--color-border-default)' }}>
              <th style={{ padding: 8 }}>{t('m19.impersonation.colTarget')}</th>
              <th style={{ padding: 8 }}>{t('m19.impersonation.colStartedBy')}</th>
              <th style={{ padding: 8 }}>{t('m19.impersonation.colReason')}</th>
              <th style={{ padding: 8 }}>{t('m19.impersonation.colStatus')}</th>
              <th style={{ padding: 8 }}>{t('m19.impersonation.colStarted')}</th>
              <th style={{ padding: 8 }}>{t('m19.impersonation.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => {
              const active = s.status.toUpperCase() === 'ACTIVE';
              const disabled = busyId === s.privilegedAccessId;
              return (
                <tr key={s.privilegedAccessId} data-testid={`impersonation-row-${s.privilegedAccessId}`}
                    style={{ borderBottom: '1px solid var(--color-bg-muted)' }}>
                  <td style={{ padding: 8 }}>{personLabel(s.subjectName, s.subjectCode) ?? '—'}</td>
                  <td style={{ padding: 8 }}>{personLabel(s.createdByName, s.createdByCode) ?? '—'}</td>
                  <td style={{ padding: 8 }}>{s.accessReason}</td>
                  <td style={{ padding: 8, color: statusColor(s.status), fontWeight: 600 }}>{s.status}</td>
                  <td style={{ padding: 8 }}>{at(s.grantedAt)}</td>
                  <td style={{ padding: 8 }}>
                    {active ? (
                      <button data-testid={`stop-${s.privilegedAccessId}`} disabled={disabled}
                              onClick={() => stop(s.privilegedAccessId)}
                              style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--color-danger)', background: 'var(--color-bg-surface)', color: 'var(--color-danger)' }}>{t('m19.impersonation.stop')}</button>
                    ) : (
                      <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </main>
  );
}
